// Shared Home-page JSON-LD (ProfessionalService + FAQPage), consumed by both
// src/pages/Home.tsx (client-side, via useSeoHead) and prerenderRoutes.ts
// (build-time, baked into the static HTML) so the two never drift apart.
import { FAQS, TRANSLATIONS } from './constants';
import { SITE_URL, getRoutePair } from './seoRoutes';

const HOME = getRoutePair('home');
const LOGO_URL = `${SITE_URL}/regenerated_image_1777641628772.webp`;

// FAQ answers carry minimal inline HTML (mostly <a> links), rendered via
// dangerouslySetInnerHTML in HowAustriaWorks.tsx. Stripped down to the same
// plain text a visitor actually sees, for the FAQPage entries below.
const stripHtml = (html: string) => html.replace(/<[^>]+>/g, '');

export function buildHomeJsonLd(language: 'EN' | 'DE'): object[] {
  const copy = language === 'EN' ? HOME.en : HOME.de;
  const t = language === 'EN' ? TRANSLATIONS.EN : TRANSLATIONS.DE;
  // Pascal Schraml is always the first team member (see constants.ts) — reuse
  // his name/role exactly as shown in the Team section further down the page.
  const founder = t.team.members[0];
  const faqs =
    ((t.knowledgeHub as { faqs?: { question: string; answer: string }[] }).faqs) || FAQS;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'AccountingService',
      name: 'PSTax',
      legalName: 'PSTax Steuerberatung FlexCo',
      url: `${SITE_URL}${copy.path}`,
      logo: LOGO_URL,
      image: LOGO_URL,
      telephone: '+43 699 1159 9396',
      email: 'office@pstax.at',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Biberhaufenweg 78',
        postalCode: '1220',
        addressLocality: language === 'EN' ? 'Vienna' : 'Wien',
        addressCountry: 'AT',
      },
      areaServed: { '@type': 'Country', name: 'Austria' },
      sameAs: ['https://www.instagram.com/pascal_pstax/'],
      founder: {
        '@type': 'Person',
        name: founder.name,
        jobTitle: founder.role,
        memberOf: {
          '@type': 'Organization',
          name:
            language === 'EN'
              ? 'Chamber of Tax Advisers and Auditors (KSW)'
              : 'Kammer der Steuerberater:innen und Wirtschaftsprüfer:innen (KSW)',
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: stripHtml(f.answer) },
      })),
    },
  ];
}
